/**
 * Действия аккаунта: регистрация, вход, восстановление пароля, выгрузка,
 * удаление и выход.
 *
 * Вынесено из `useAppFlow`: тот перешагнул порог разделения в 600 строк, а
 * действия аккаунта — связный кусок, который читается сам по себе. Экранам
 * они по-прежнему приходят одним объектом потока.
 */

import { useCallback, useRef } from 'react';
import type { LanguageCode } from '@type2x/shared-contracts';
import {
  forgetRegistrationClaim,
  readRegistrationClaim,
  rememberRegistrationClaim,
} from '@type2x/adapter-web';
import {
  attachWorkspaceToUser,
  claimConfirmedRegistration,
  confirmPasswordReset,
  deleteAccount,
  exportProgress,
  loginAndClaim,
  logout,
  readSignedIn,
  registerAndClaim,
  requestPasswordReset,
  resendVerification,
} from '@type2x/feature-accounts';
import type { SignedIn } from '@type2x/feature-accounts';
import { updatePrivacy } from '@type2x/feature-personal-workspace';

import type { Runtime } from '../runtime.js';
import { restoreWorkspace } from '../state/restore.js';
import type { AppFlow, WorkspaceAccess } from './useAppFlow.js';
import type { useSync } from './useSync.js';

export function useAccountActions(
  runtime: Runtime,
  access: WorkspaceAccess,
  sync: ReturnType<typeof useSync>,
  /** Язык интерфейса: с него начинается восстановленное пространство. */
  uiLanguage: LanguageCode,
): Pick<
  AppFlow,
  | 'onRegister'
  | 'onLogin'
  | 'onReadSession'
  | 'onResendVerification'
  | 'onRequestPasswordReset'
  | 'onConfirmPasswordReset'
  | 'onExportProgress'
  | 'onDeleteAccount'
  | 'onLogout'
  | 'onConsentChange'
> {
  const { workspace, commit, forget } = access;

  const onRegister = useCallback(
    async (email: string, password: string) => {
      if (workspace === null) throw new Error('Пространство ещё не загружено');

      const outcome = await registerAndClaim(
        runtime.accounts,
        { email, password },
        runtime.installationId,
        runtime.ids.newSyncId(),
      );

      // Пока письмо не подтверждено, аккаунта у прогресса нет: переносить
      // его некому, и очередь по-прежнему ждёт. Секрет браузера остаётся
      // здесь: подтвердить адрес могут с телефона, а сессия нужна и тут.
      if (outcome.kind === 'awaiting-verification') {
        if (outcome.claimToken !== null) rememberRegistrationClaim(outcome.claimToken);
        return outcome;
      }

      // Локальный прогресс не пересоздаётся: меняется только владелец.
      await commit(attachWorkspaceToUser(workspace, outcome, runtime.clock.isoNow()));
      // Появился аккаунт — накопленная гостем очередь наконец уходит.
      await sync.flush();
      return outcome;
    },
    [runtime, workspace, commit, sync],
  );

  const onLogin = useCallback(
    async (email: string, password: string) => {
      const outcome = await loginAndClaim(
        runtime.accounts,
        { email, password },
        // Переносить нечего, пока пространства нет: вход возможен и до того,
        // как в этом браузере что-то набирали.
        workspace === null
          ? null
          : { installationId: runtime.installationId, syncId: runtime.ids.newSyncId() },
      );

      // Прогресс, принадлежащий другому аккаунту, вошедшему не достаётся:
      // сменить у него владельца значило бы отдать чужое.
      if (workspace !== null && outcome.progress !== 'taken') {
        await commit(attachWorkspaceToUser(workspace, outcome, runtime.clock.isoNow()));
        await sync.flush();
        return outcome;
      }

      // Переносить нечего — значит, надо забрать. Здесь человек входит на
      // другом устройстве или после очистки браузера: аккаунт помнит его
      // навык, и до этого этапа не возвращал его никогда.
      if (workspace === null) {
        const now = runtime.clock.isoNow();
        const restored = restoreWorkspace(
          (await runtime.sync.fetchProgress()) ?? [],
          outcome.userId,
          runtime.installationId,
          uiLanguage,
          now,
        );
        if (restored !== null) await commit(restored);
      }

      return outcome;
    },
    [runtime, workspace, commit, sync, uiLanguage],
  );

  const onResendVerification = useCallback(
    async (email: string) => {
      await resendVerification(runtime.accounts, email);
    },
    [runtime],
  );

  // Состояние аккаунта берётся у сервера, а не из памяти вкладки: после
  // перезагрузки экран обязан знать, вошёл ли человек и подтверждён ли адрес.
  //
  // Сессии может не быть по разной причине. Одна из них — подтверждение,
  // сделанное на другом устройстве: cookie досталась ему, а этот браузер
  // держит секрет, выданный при регистрации. Тогда он забирает свою сессию
  // сам, и секрет больше не нужен.
  //
  // Секрет одноразовый, а спрашивающих в одной вкладке двое: шапка и экран
  // аккаунта. Поэтому потративший его ответ запоминается — иначе второй
  // спрашивающий получил бы «вы не вошли» ровно потому, что первый уже вошёл.
  const claimedSession = useRef<SignedIn | null>(null);
  const onReadSession = useCallback(async () => {
    const signedIn = await readSignedIn(runtime.accounts);
    if (signedIn !== null) return signedIn;
    if (claimedSession.current !== null) return claimedSession.current;

    const claim = readRegistrationClaim();
    if (claim === null) return null;

    const claimed = await claimConfirmedRegistration(runtime.accounts, claim);
    if (claimed !== null) {
      forgetRegistrationClaim();
      claimedSession.current = claimed;
    }

    return claimed;
  }, [runtime]);

  const onRequestPasswordReset = useCallback(
    async (email: string) => {
      await requestPasswordReset(runtime.accounts, email);
    },
    [runtime],
  );

  const onConfirmPasswordReset = useCallback(
    async (token: string, password: string) => {
      await confirmPasswordReset(runtime.accounts, token, password);
    },
    [runtime],
  );

  const onExportProgress = useCallback(async () => {
    const data = await exportProgress(runtime.accounts);
    const day = runtime.clock.isoNow().slice(0, 10);
    runtime.files.save(`type2x-progress-${day}.json`, data);
  }, [runtime]);

  const onDeleteAccount = useCallback(
    async (password: string) => {
      await deleteAccount(runtime.accounts, password);
      // Аккаунта больше нет, и держать его прогресс в этом браузере значило бы
      // не выполнить обещание удаления.
      await forget();
    },
    [runtime, forget],
  );

  /*
   * Выход из аккаунта (задание §15).
   *
   * Маршрут `POST /auth/logout` был написан с самого начала и не вызывался ни
   * разу: войти можно было, выйти — нет. Оставалось удалить аккаунт
   * (необратимо) или чистить cookie руками.
   *
   * Локальное пространство забывается вместе с сессией. Оставить его значило
   * бы отдать прогресс следующему за этим браузером, а его регистрация
   * присвоила бы чужое: `attachWorkspaceToUser` привязывает то, что нашла.
   * Терять при этом нечего — с этапа ABR навык возвращается при входе.
   *
   * Нечего, кроме одного: итогов занятий, которые ещё не ушли на сервер. Их не
   * вернёт никто, поэтому сначала очередь разбирается, а если разобралась не
   * вся — выход останавливается и говорит об этом. Настоять можно, но зная.
   */
  const onLogout = useCallback(
    async (force = false): Promise<'done' | 'pending'> => {
      const drained = await sync.flush();
      if (!drained && !force) return 'pending';

      await logout(runtime.accounts);
      await forget();
      return 'done';
    },
    [runtime, sync, forget],
  );

  const onConsentChange = useCallback(
    (optIn: boolean) => {
      if (workspace === null) return;
      void commit(updatePrivacy(workspace, { leaderboardOptIn: optIn }, runtime.clock.isoNow()));

      // Согласие — договор с сервером, а не галочка в браузере: без этого
      // запроса в таблицу человека никто не поставит (ТЗ FR-LB-06). Гостю
      // сервер отвечает «требуется вход», и это не ошибка приложения:
      // участвовать без аккаунта нечем. Настоящее состояние участия
      // показывает экран рейтинга — по ответу сервера, а не по этой галочке.
      void runtime.leaderboard
        .updateParticipation({ language: workspace.activeLanguage, optIn })
        .catch(() => undefined);
    },
    [runtime, workspace, commit],
  );

  return {
    onRegister,
    onLogin,
    onReadSession,
    onResendVerification,
    onRequestPasswordReset,
    onConfirmPasswordReset,
    onExportProgress,
    onDeleteAccount,
    onLogout,
    onConsentChange,
  };
}
